import React, { Component } from 'react';
import PropTypes from 'prop-types'
import './LocationInputBox.sass';
import Geosuggest from 'react-geosuggest';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty'
import includes from 'lodash/includes';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { connect } from 'react-redux';

const getMapsAddressComponent = (address_components, type) => {
    return find(address_components, (c) => { return includes(c.types, type); });
}
const getCustomClassName = (type) => (type ? `location-field-${type.toLowerCase()}` : '');

export class LocationInputBox extends Component {
    constructor(props) {
        super(props);

        this.handleSelection = this.handleSelection.bind(this);
        this.clear = this.clear.bind(this);
        this.blur = this.blur.bind(this);
        const deliveryAddress = get(props, 'deliveryAddress', {});
        const addressType = get(deliveryAddress, 'type', '').toLowerCase();
        this.state = {
            errorMessage: '',
            userInputAddress: deliveryAddress,
            customClassName: `location-field-${addressType}`
        };
    }

    componentDidMount() {
        if(this.props.setFocus) {
            this.refs.geoSuggest.focus();
        }
        this.mounted = true;
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    getAddressInfo(e) {
        const gmaps = e.gmaps;
        const location = e.location;
        const components = gmaps && gmaps.address_components;
        const street_number_component = getMapsAddressComponent(components, 'street_number');
        const route_component = getMapsAddressComponent(components, 'route');
        const city_component = getMapsAddressComponent(components, 'locality');
        const state_component = getMapsAddressComponent(components, 'administrative_area_level_1');
        const zip_code_component = getMapsAddressComponent(components, 'postal_code');

        if (!route_component || !city_component || !state_component || !zip_code_component) {
            this.setState({ errorMessage: 'The selected address is not valid.' });
        } else {
            this.setState({ errorMessage: '' });
            let address_1;

            if (street_number_component) {
                address_1 = `${street_number_component.short_name} ${route_component.short_name}`;
            } else {
                address_1 = `${route_component.short_name}`;
            }

            return {
                location,
                address_components: {
                    address_1,
                    city: city_component.long_name,
                    state: state_component.short_name,
                    zip_code: zip_code_component.short_name,
                },
                address: gmaps.formatted_address,
            };
        }
    }

   /**
   * Allow for direct access to user input
   *
   * This is necessary becuase google suggestions started returning placeIds
   * that were not found in the geocoding service. This would leave our
   * customers stranded if the place had been sweeped out of the geocoder
   *
   * refs: https://code.google.com/p/gmaps-api-issues/issues/detail?id=11107#c1
   *
   */
    getUserInput() {
        const geo = this.refs.geoSuggest
        return geo.state.userInput
    }

    handleSelection(e) {
        if (typeof e === 'undefined') {
            return
        }

        const { savedAddresses, onAddressChange } = this.props;
        const isFixture =  e.isFixture && !isNil(e.deliveryAddress);
        const addrInfo = isFixture ? e.deliveryAddress : { ...this.getAddressInfo(e) };

        const matchedSavedAddress = find(savedAddresses, (savedAddr) => {
            const sameLoc = isEqual(
                get(e, 'location'),
                get(savedAddr, 'delivery_address.location')
            );
            const sameAddr = isEqual(
                get(e, 'placeId'),
                get(savedAddr, 'delivery_address.address')
            );
            return sameLoc || sameAddr;
        });
        const type = get(matchedSavedAddress, 'address_type') || get(addrInfo, 'address_type');

        const newState = type ? {
            userInputAddress: { ...addrInfo, type },
            customClassName: getCustomClassName(type)
        } : {
            userInputAddress: addrInfo,
            customClassName: ''
        };
        if (this.mounted) {
            this.setState(
                newState,
                () => {onAddressChange(newState.userInputAddress)}
            );
        }
    }

    update(addr='') {
        this.refs.geoSuggest.update(addr);
    }

    clear() {
        const { onAddressChange } = this.props;
        this.update('');
        if (this.mounted) {
            this.setState(
                { customClassName: '' },
                () => {onAddressChange(null)}
            );
        }
    }

    blur() {
        const { userInputAddress } = this.state;
        const { onAddressChange } = this.props;
        this.update(userInputAddress.address);
        const type = get (userInputAddress, 'type');
        const customClassName = getCustomClassName(type);
        if (this.mounted) {
            this.setState(
                { customClassName },
                () => {onAddressChange(userInputAddress)}
            );
        }
    }

    render() {
        const {
            placeholder,
            savedAddresses,
            deliveryAddress,
            order,
            currentPlace,
            selectedDropoff,
            baseClass = ''
        } = this.props;

        const { customClassName, errorMessage } = this.state;
        const hasErrors = !isEmpty(errorMessage.toString());
        let initialValue = get(deliveryAddress, 'address', '').toString();
        let fixtures = [];

        if (currentPlace) {
            const placeName = get(currentPlace, 'name', '');
            const dropoff = get(selectedDropoff, 'name', '');
            const label = `${placeName} ${dropoff ? `(${dropoff})` : ''}`;
            const orderLocation = get(order, 'delivery_address.location', {});
            const location = get(deliveryAddress, 'location');

            if (location && isEqual(orderLocation, location)) {
                fixtures = [
                    ...fixtures,
                    {
                        className: 'fixture place-fixture',
                        label,
                        location,
                        deliveryAddress
                    }
                ];
                initialValue = label;
            }
        }

        if (!isEmpty(savedAddresses)) {
            const workAddress = get(savedAddresses, 'work.delivery_address');
            const work = get(workAddress, 'address');
            if (work) {
                fixtures = [
                    ...fixtures,
                    {
                        className: 'fixture work-fixture',
                        label: work,
                        deliveryAddress: { ...workAddress, address_type: 'work' }
                    }
                ];
            }
            const homeAddress = get(savedAddresses, 'home.delivery_address');
            const home = get(homeAddress, 'address');
            if (home) {
                fixtures = [
                    ...fixtures,
                    {
                        className: 'fixture home-fixture',
                        label: home,
                        deliveryAddress: { ...homeAddress, address_type: 'home' }
                    }
                ];
            }
        }
        return (
            <div className="location-bar-container">
                <Geosuggest
                    ref='geoSuggest'
                    placeDetailFields={['formatted_address', 'address_component', 'name']}
                    className={`${baseClass} location-field header-location-field ${customClassName ? customClassName : ''}`}
                    {...{ placeholder, fixtures, initialValue }}
                    onFocus={this.clear}
                    onBlur={this.blur}
                    onSuggestSelect={this.handleSelection} />

                {hasErrors ? <div className="gmaps-error">{errorMessage}</div> : null}
            </div>
        );
    }
}
LocationInputBox.propTypes = {
    address: PropTypes.object,
    onAddressChange: PropTypes.func.isRequired,
    savedAddresses: PropTypes.object,
    deliveryAddress: PropTypes.object,
    placeholder: PropTypes.string
};

export default connect(
    (state) => ({
       order: get(state.orderReducer, 'order'),
       savedAddresses: get(state.locationReducer, 'savedAddresses', {}),
       currentPlace: get(state.locationReducer, 'currentPlace'),
       selectedDropoff: get(state.locationReducer, 'selectedDropoff')
     }),
    {},
    null,
    { forwardRef: true }
  )(LocationInputBox);
